// LeagueControls.js — what /all-time-table and /race share: the season range
// kept in the URL (?from=&to=, plus any other keys a page adds), the season
// pickers with All-time / this-season shortcuts, and the dark field style.

import React, { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Chip, TextField, Typography } from "@mui/material";
import { BRAND } from "../../theme";
import { StatusFilter } from "./ClubFilters";
import { seasonText } from "./data";

// URL-backed page state. `update` builds on the newest params, including
// changes not rendered yet, so two quick edits (From, then To) don't
// overwrite each other.
export function useLeagueParams(data) {
  const [params, setParams] = useSearchParams();
  const latest = useRef({ key: null, params });
  if (params.toString() !== latest.current.key) latest.current = { key: params.toString(), params };

  const first = data.seasons[0];
  const last = data.currentSeason;
  const clamp = (v, fallback) => (Number.isInteger(v) && v >= first && v <= last ? v : fallback);
  const rangeOf = (sp) => {
    let f = clamp(parseInt(sp.get("from"), 10), first);
    let t = clamp(parseInt(sp.get("to"), 10), last);
    if (f > t) [f, t] = [t, f];
    return [f, t];
  };
  const [from, to] = rangeOf(params);

  const update = (changes) => {
    const next = new URLSearchParams(latest.current.params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    }
    latest.current = { key: next.toString(), params: next };
    setParams(next, { replace: true });
    return next.toString();
  };
  const setRange = (f, t) => update({ from: f === first ? null : f, to: t === last ? null : t });
  const current = () => rangeOf(latest.current.params);
  return { params, first, last, from, to, update, setRange, current };
}

export const darkField = (extra) => ({
  ...extra,
  "& .MuiOutlinedInput-root": {
    background: "rgba(20,20,26,0.7)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
    "&:hover fieldset, &.Mui-focused fieldset": { borderColor: BRAND.cyan },
  },
  "& .MuiInputLabel-root": { color: BRAND.textSecondary },
  "& .MuiInputLabel-root.Mui-focused": { color: BRAND.cyan },
  "& input, & select": { color: "#fff", fontSize: 14 },
  "& select option": { background: "#16161d", color: "#fff" },
  "& .MuiSvgIcon-root": { color: BRAND.textSecondary },
});

// Native select: fast, and the phone's own picker on mobile.
function SeasonPicker({ id, label, value, seasons, onChange }) {
  return (
    <TextField
      id={id}
      select
      size="small"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      SelectProps={{ native: true }}
      inputProps={{ "aria-label": label }}
      sx={darkField({ width: 112 })}
    >
      {seasons.map((s) => (
        <option key={s} value={s}>
          {seasonText(s)}
        </option>
      ))}
    </TextField>
  );
}

// Seasons From / To with shortcuts, and the All / this season / Former filter.
export function SeasonBar({ data, lp, status, onStatus, counts }) {
  const { first, last, from, to, setRange, current } = lp;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        mb: 1,
        p: 1.5,
        borderRadius: 3,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography component="span" sx={{ color: BRAND.textSecondary, fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          Seasons
        </Typography>
        <SeasonPicker id="season-from" label="From season" value={from} seasons={data.seasons} onChange={(v) => setRange(v, Math.max(v, current()[1]))} />
        <Typography component="span" sx={{ color: BRAND.textSecondary, fontSize: 13 }}>
          to
        </Typography>
        <SeasonPicker id="season-to" label="To season" value={to} seasons={data.seasons} onChange={(v) => setRange(Math.min(current()[0], v), v)} />
        {[
          ["All-time", first, last],
          [seasonText(last), last, last],
        ].map(([label, f, t]) => (
          <Chip
            key={label}
            label={label}
            size="small"
            clickable
            onClick={() => setRange(f, t)}
            sx={{
              fontWeight: 700,
              color: from === f && to === t ? "#fff" : BRAND.textSecondary,
              background: from === f && to === t ? "rgba(111,93,245,0.35)" : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          />
        ))}
      </Box>
      <Box sx={{ ml: { md: "auto" } }}>
        <StatusFilter value={status} onChange={onStatus} counts={counts} currentLabel={`In ${seasonText(data.currentSeason)}`} />
      </Box>
    </Box>
  );
}

export const rangeLabelOf = (from, to, first, last) =>
  from === first && to === last ? "All-time" : from === to ? seasonText(from) : `${seasonText(from)} to ${seasonText(to)}`;
